import Button from '../Button/Button'
import NoDataFound from '../NoDataFound/NoDataFound'

/**
 * Full-page "not accessible" state for any route the user can reach but should not use right now
 * (for example a canceled shipment's upload page). Same layout as `NoPermissionEmptyState`.
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.title] - Main heading.
 * @param {import('react').ReactNode} [props.message] - Reason shown under the heading.
 * @param {string} [props.actionText] - Button label; the button is hidden without `onAction`.
 * @param {() => void} [props.onAction] - Button click handler, e.g. navigate back.
 * @param {string} [props.dataTestId] - Root test id.
 * @example
 * <PageNotAccessible
 *   message="Temperature data can't be added to a canceled shipment."
 *   actionText="Back to shipment details"
 *   onAction={() => navigate(detailsPath)}
 * />
 * @since product-release-v1.1
 */
const PageNotAccessible = ({
  title = 'This page is not accessible.',
  message,
  actionText = 'Go back',
  onAction,
  dataTestId = 'PageNotAccessible-component',
}) => (
  <div
    className="w-full h-full flex items-center justify-center bg-BG-Default-neutral-00 z-[999999]"
    data-testid={dataTestId}
  >
    <div className="flex flex-1 flex-col justify-center items-center">
      <NoDataFound
        title={
          <div className="mb-6">
            {title}
            {message ? (
              <>
                <br />
                {message}
              </>
            ) : null}
          </div>
        }
        subtitle={
          onAction ? (
            <Button
              type="primary"
              className="text-base w-fit py-2 px-4"
              onClick={onAction}
              text={actionText}
            />
          ) : null
        }
        data-testid="page-not-accessible-no-data-found"
      />
    </div>
  </div>
)

export default PageNotAccessible
